import React, { useState, FC, useEffect } from 'react';
import { useHistory } from 'react-router';
import styled from '@emotion/styled';
import SalesInfo from './Components/SalesInfo';
import { useQuery } from '@apollo/client';
import { useReportsApi, useReportsUniverse } from '~Components/ReportsUniverse';
import {
  useDispatch,
  useSelector,
  useI18n,
  useCashRegister,
  useLogger,
  useCurrency,
  useDates,
  CashRegisterTotals,
  useLicenses,
} from '~Hooks';
import * as actions from '~actions';
import { useCashApi, useCashUniverse } from '~Components/CashUniverse';
import { Languages } from '~Types';
import printNorwayZReport from '~Scenes/Zreports/Printed/Norway';
import printGeneralZReport from '~Scenes/Zreports/Printed/General';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useStorage } from '~Components/Storage';
import { DateTime } from 'luxon';
import {
  GetVenueTableEventsSnapshots,
  GetVenueTableEventsSnapshotsVariables,
} from '~Scenes/Reports/__queries__';
import { CountAndAmount } from '@baemingo/report-events/dist/types';
import { Cheque } from '~Scenes/Reports';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import { Box } from '~Styles';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { ALL_CASH_EVENTS_QUERY, ITEMS_QUERY, TABLE_EVENTS_SNAPSHOTS_QUERY } from './api/queries';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import {
  GetAllCashEvents,
  GetAllCashEventsVariables,
  GetItems,
  GetItemsVariables,
} from './api/__queries__';
import EndOfDayHandy from './Components/EndOfDayHandy';
import EnterCashBalance from './Components/EnterCashBalance';
import useModal from '~ComponentsImproved/Modal/useModal';

const LANGUAGE_TO_COUNTRY: Record<Languages, string> = {
  en: 'GB',
  se: 'SE',
  es: 'ES',
  no: 'NO',
  dk: 'DK',
  tr: 'TR',
};

const Wrapper = styled(Box)`
  flex: 1;
  height: calc(100vh - 64px);

  ${handyBreakpoint} {
    margin: 0;
    padding: 0px 16px;
    min-height: 100%;
    height: 100%;
    box-sizing: border-box;
    background: var(--colorBlueGray1200);
  }
`;

const ScrollWrapper = styled(Box)`
  overflow-y: auto;
  padding: 20px 40px 20px 20px;
  box-sizing: border-box;
`;

export const BackButtonIcon = styled.i`
  font-size: 15px;
  font-weight: bold !important;
  color: var(--ColorBlack);
`;

export type Item = {
  itemId: string;
  count: number | null;
  outOfStock: boolean;
  EODAmount: string | number;
  defaultPrice: number;
  name?: string;
  venueId: string;
};

interface Props {
  isHandy?: boolean;
  onCompleted?: () => void;
}

/*
 *  This component manages the End of Day (EOD) process, enabling users to manually
 *  close the day by recording cash (if required) and printing a Z-report.
 *  When `enableAutoCashEOD` is true, this component is hidden, as the backend
 *  automatically handles both the cash register and report closings.
 */
const EndOfDay: FC<Props> = ({ isHandy, onCompleted }) => {
  const { i18n } = useI18n();

  const history = useHistory();
  const dates = useDates();
  const reportsApi = useReportsApi();
  const reportsUniverse = useReportsUniverse();
  const currency = useCurrency();
  const logger = useLogger('end-of-day');
  const dispatch = useDispatch();
  const cashApi = useCashApi();
  const cashRegister = useCashRegister();
  const [storage] = useStorage();
  const licenses = useLicenses();

  const venueId = useSelector((state) => state.venueId) as string;
  const hasIndividualEOD = useSelector((state) => state.hasIndividualEOD);

  const [enteredCashOut, setEnteredCashOut] = useState<number>(0);
  const [printingReport, setPrintingReport] = useState<boolean>(false);
  const [cashAmountEntered, setCashAmountEntered] = useState<boolean>(false);
  const [payouts, setPayouts] = useState<{ [key: string]: number }>({});

  const enterEODModal = useModal();

  const shouldNotEnterCash = hasIndividualEOD && storage.disableCash;

  // Report date is set to 5:30am of previous day if it is before 5:30am else it is set to 5:30am of current day
  const reportDate =
    DateTime.local() < DateTime.local().set({ hour: 5, minute: 30, second: 1 })
      ? DateTime.local()
          .minus({ days: 1 })
          .set({ hour: 5, minute: 30, second: 1 })
          .toFormat('yyyy-MM-dd HH:mm:ss')
      : DateTime.local()
          .set({ hour: 5, minute: 30, second: 1 })
          .toFormat('yyyy-MM-dd HH:mm:ss');

  const itemsQuery = useQuery<GetItems, GetItemsVariables>(ITEMS_QUERY, {
    variables: {
      venueId,
    },
  });

  const currentDate = new Date();

  const cashQuery = useQuery<GetAllCashEvents, GetAllCashEventsVariables>(ALL_CASH_EVENTS_QUERY, {
    variables: {
      filter: { venue: { id: venueId } },
      sentAfter: dates.getAccountingStartDate(currentDate),
      sentBefore: dates.getAccountingEndDate(currentDate),
      venueId,
    },
    fetchPolicy: 'network-only',
    skip: !venueId,
  });

  const tableSnapshotQuery = useQuery<
    GetVenueTableEventsSnapshots,
    GetVenueTableEventsSnapshotsVariables
  >(TABLE_EVENTS_SNAPSHOTS_QUERY, {
    variables: {
      venueId: venueId,
      date: dates.getAccountingEndDate(currentDate), // What tables looked like at the end of the day
    },
    skip: !venueId || !licenses.hasTSR,
    fetchPolicy: 'network-only',
  });

  const cheques =
    (tableSnapshotQuery.data?.Venue?.eventsSnapshot?.cheques as Cheque[] | undefined) ?? [];

  /** If user has TSR --> Go through all cheques to sum them up with count & amount */
  const openCheques: CountAndAmount | null = licenses.hasTSR
    ? cheques
        // Filter out cheques created in the POS mode in handy
        .filter((c) => !c.tableId?.startsWith('POS_'))
        .filter((c) => c.tableId !== PARKED_ORDER_TABLE_ID) // Filter out cheques that is parked orders
        .reduce(
          (acc, c) => ({
            count: acc.count + 1,
            amount: acc.amount + (c.amount - c.discount - c.paidAmount),
          }),
          { count: 0, amount: 0 },
        )
    : null;

  const handleCashOut = (value: number) => {
    setEnteredCashOut(Math.round(value * 100));
    setCashAmountEntered(true);
    cashApi.endDay(Math.round(value * 100));
    logger.info('EOD: Cash out completed', { value });
  };

  const printReport = (cashRegisterData: CashRegisterTotals) => {
    const companyInfo = itemsQuery.data?.venue?.company;
    const country =
      companyInfo?.country ??
      (companyInfo?.defaultLanguage
        ? LANGUAGE_TO_COUNTRY[companyInfo?.defaultLanguage as Languages]
        : 'SE');

    const props = {
      reportTitle: i18n('ZReport.Title'),
      venueName: itemsQuery.data?.venue?.name ?? '',
      venueLocation: itemsQuery.data?.venue?.location,
      venueOrg: itemsQuery.data?.venue?.organizationId ?? '',
      companyInfo: itemsQuery.data?.venue?.company,
      taxComplianceDeviceName: storage.taxComplianceDeviceName || 'Unknown',
      reportDate: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
      data: reportsUniverse,
      cashRegister: {
        cashInitial: cashRegisterData.cashInitial,
        cashSalesIncTips: cashRegisterData.cashSalesIncTips,
        cashDeposits: cashRegisterData.cashDeposits,
        cashWithdrawals: cashRegisterData.cashWithdrawals,
        cashFinal: enteredCashOut,
      },
      i18n,
      formatCurrency: currency.format,
      categories: itemsQuery.data?.venue?.company?.categories ?? [],
      staffs: itemsQuery.data?.venue?.company?.staffs ?? [],
      payouts,
      openCheques,
    };

    if (country === 'NO') {
      printNorwayZReport({
        ...props,
      });
    } else {
      printGeneralZReport({
        ...props,
      });
    }
  };

  const handleFinish = async () => {
    setPrintingReport(true);

    try {
      const cashRegisterData = await cashRegister.getLatestDataForDate(reportDate);
      printReport(cashRegisterData);
      setPrintingReport(false);
    } catch (e) {
      printReport({
        cashInitial: 0,
        cashSalesIncTips: 0,
        cashDeposits: 0,
        cashWithdrawals: 0,
        cashFinal: 0,
      });
      logger.error('EOD: Error while getting cash register data', { errorMessage: e });
      setPrintingReport(false);
    }

    reportsApi.closeDay();

    dispatch(actions.addToast(i18n('TOAST.EOD.Done'), true));
    logger.info('EOD: Completed successfully');

    !isHandy && history.push('gateway');
    onCompleted && onCompleted();
  };

  useEffect(() => {
    if (!cashQuery.loading && cashQuery.data) {
      const allDevices = cashQuery.data.allPOSDevices || [];

      // Filter devices based on showVenueReport
      const filteredDevices = allDevices.filter(
        (device: any) => device.id === storage.taxComplianceDeviceId,
      );

      // Parse payouts for all filtered devices
      const parsedPayouts = getParsedPayouts(filteredDevices);
      setPayouts(parsedPayouts);
    }
  }, [cashQuery.data, cashQuery.loading]);

  const getParsedPayouts = (devices: any[]) => {
    const result: { [key: string]: number } = {};

    devices.forEach((device) => {
      device?.cashEvents?.events?.items
        .filter((event: any) => event.type === 'CASH_CHANGE' && event.amount < 0 && event.sourceId)
        .forEach((event: any) => {
          const sourceName =
            itemsQuery.data?.venue?.cashSources.find((s) => s.id === event.sourceId)?.name ||
            'DRAWER';
          result[sourceName] = (result[sourceName] || 0) + -event.amount;
        });
    });

    return result;
  };

  const loading = itemsQuery.loading || cashQuery.loading || tableSnapshotQuery.loading;

  return (
    <>
      {isHandy ? (
        <EndOfDayHandy
          loading={loading}
          cashAmountEntered={shouldNotEnterCash ? true : cashAmountEntered}
          enteredCashOut={enteredCashOut}
          printingReport={printingReport}
          handleEnterEOD={() => enterEODModal.show()}
          handleFinish={handleFinish}
        />
      ) : (
        <Wrapper
          display="flex"
          justify-content="center"
          alignItems="center"
          flexDirection="column">
          <BackTopBar title={i18n('EOD.EndOfDay')} />

          <ScrollWrapper>
            {loading ? (
              <Box
                style={{ height: '50vh' }}
                display="flex"
                alignItems="center"
                flexDirection="column"
                gap="small"
                justifyContent="center">
                <Loader color="var(--colorBlueGray1200)" />
                <Paragraph level={3}>{i18n('EOD.Loading')}</Paragraph>
              </Box>
            ) : (
              <SalesInfo
                enteredCashOut={enteredCashOut}
                handleEnterEOD={() => enterEODModal.show()}
                printingReport={printingReport}
                handleFinish={handleFinish}
                tenders={itemsQuery.data?.venue?.cashSources ?? []}
                cashAmountEntered={shouldNotEnterCash ? true : cashAmountEntered}
              />
            )}
          </ScrollWrapper>
        </Wrapper>
      )}

      <EnterCashBalance
        isShowing={enterEODModal.isShowing}
        handleCashOut={handleCashOut}
        isHandy={isHandy}
        onClose={() => enterEODModal.hide()}
      />
    </>
  );
};

export default EndOfDay;
